// Relative imports, not the '@' alias: jest has no moduleNameMapper for '@'
// and jest.config.js may not be touched. See useGeneratedCourse.ts.
import ChessRestClient from '../../Api/ChessRestClient';
import {getLocalStorage} from '../../Utils/CommonUtils';
import {useMutation, useQuery, UseMutationResult, UseQueryResult} from '@tanstack/react-query';

import {CourseJob, CourseList} from './types';

async function authHeader() {
  const token = await getLocalStorage('access_token');
  return {Authorization: `Bearer ${token}`};
}

export async function fetchCourseList(): Promise<CourseList> {
  const response = await ChessRestClient.get('/video_learning/courses/', {
    headers: await authHeader(),
  });
  return response.data as CourseList;
}

/**
 * Every generated course, newest first. Unlike the course itself this carries
 * no presigned URLs, so it is safe to keep around and cheap to refetch.
 */
export function useCourseList(): UseQueryResult<CourseList, Error> {
  return useQuery<CourseList, Error>(['generated-course-list'], fetchCourseList, {
    staleTime: 60 * 1000,
  });
}

export type CreateCoursePayload = {video_url?: string; video_course_id?: number; force?: boolean};

export async function createCourse(payload: CreateCoursePayload): Promise<CourseJob> {
  const response = await ChessRestClient.post(
    '/video_learning/courses/create/',
    payload,
    {headers: await authHeader()},
  );
  return response.data as CourseJob;
}

export function useCreateCourse(): UseMutationResult<CourseJob, Error, CreateCoursePayload> {
  return useMutation<CourseJob, Error, CreateCoursePayload>(createCourse);
}

export async function fetchCourseJob(videoCourseId: number): Promise<CourseJob> {
  const response = await ChessRestClient.get(
    `/video_learning/courses/${videoCourseId}/status/`,
    {headers: await authHeader()},
  );
  return response.data as CourseJob;
}

/**
 * Polls one generation while it runs.
 *
 * A real masterclass takes long enough that the screen has to be able to be
 * closed and reopened, so the job is keyed by the recording rather than held
 * in component state. Polling stops once the job reaches a terminal state —
 * otherwise a finished course would be refetched every few seconds forever.
 */
export function useCourseJob(
  videoCourseId: number | null,
  intervalMs = 5000,
): UseQueryResult<CourseJob, Error> {
  return useQuery<CourseJob, Error>(
    ['generated-course-job', videoCourseId],
    () => fetchCourseJob(videoCourseId as number),
    {
      enabled: videoCourseId != null,
      refetchInterval: data =>
        data && (data.status === 'done' || data.status === 'failed') ? false : intervalMs,
    },
  );
}
